// Borrador local de Evaluación de Inspección para captura sin señal en sitio (ver plan
// "Captura offline de Evaluación de Inspección"). Usa IndexedDB (vía localforage) en vez de
// localStorage porque: sobrevive cerrar el navegador/dispositivo igual que localStorage, pero
// sin el límite chico de ~5-10MB y guardando objetos estructurados directo, sin serializar a
// mano.
//
// La llave NO es evaluationId -- InspectionEvaluation no existe hasta el primer guardado
// exitoso contra el servidor (ver InspectionEvaluationService.saveEvaluation), así que en el
// caso "primera vez, en sitio, sin señal" no hay id numérico todavía. Se indexa por la llave
// natural (quotationId, branchId), que sí llega por location.state desde la pantalla de
// origen sin necesitar red.
import localforage from 'localforage';
import { OFFLINE_FIELD_MODE } from '../config/features';

// Apagado (OFFLINE_FIELD_MODE=false): ni se lee ni se escribe; lo ya guardado se conserva intacto.
const enabled = OFFLINE_FIELD_MODE;

const store = localforage.createInstance({
  name: 'conem-offline',
  storeName: 'inspection_evaluations',
  description: 'Borradores locales de Evaluación de Inspección capturados sin señal'
});

// Best-effort: le pide al navegador que este storage sea "persistente" (no lo borre
// automáticamente bajo presión de espacio). No todos los navegadores lo conceden, pero no
// cuesta nada intentarlo. Se llama una sola vez al cargar este módulo.
if (enabled && typeof navigator !== 'undefined' && navigator.storage?.persist) {
  navigator.storage.persist().catch(() => { /* silencioso -- best-effort */ });
}

const draftKey = (quotationId, branchId) => `${quotationId}_${branchId ?? 'MATRIZ'}`;

const offlineEvaluationStore = {
  draftKey,

  /**
   * @returns el borrador local completo, o null si no hay nada guardado para este ente.
   * Forma: { quotationId, branchId, evaluationId, data: {...payload tipo saveEvaluation...},
   *          pendingSync: bool, lastLocalChangeAt: ISOString, startedFromServerSnapshot: bool }
   */
  async getDraft(quotationId, branchId) {
    if (!enabled || !quotationId) return null;
    try {
      return await store.getItem(draftKey(quotationId, branchId));
    } catch (err) {
      console.error('Error leyendo borrador offline de evaluación:', err);
      return null;
    }
  },

  /**
   * Guarda/actualiza el borrador completo. `startedFromServerSnapshot` distingue un borrador
   * que sí partió de una copia real del servidor (precarga exitosa) de uno que arrancó en
   * blanco por no haber señal la primera vez -- útil para diagnóstico, no cambia la regla de
   * "sobrescribe todo" ya acordada.
   */
  /**
   * Opciones extra para la detección de conflictos al sincronizar:
   * - serverUpdatedAt: updatedAt de la evaluación en el servidor cuando se tomó la copia; si al
   *   sincronizar el servidor tiene uno más nuevo, otra persona la modificó (conflicto).
   * - forceOverwrite: el usuario ya eligió "conservar mis cambios" ante un conflicto.
   * - resetEvaluationId: el borrador se guardará como evaluación nueva (la anterior ya no existe).
   */
  async saveDraft(quotationId, branchId, data, {
    evaluationId = null, pendingSync = true, startedFromServerSnapshot = null,
    serverUpdatedAt, serverVersion, forceOverwrite, resetEvaluationId = false
  } = {}) {
    if (!enabled || !quotationId) return;
    const key = draftKey(quotationId, branchId);
    try {
      const existing = await store.getItem(key);
      await store.setItem(key, {
        quotationId,
        branchId: branchId ?? null,
        evaluationId: resetEvaluationId ? null : (evaluationId ?? existing?.evaluationId ?? null),
        data,
        pendingSync,
        lastLocalChangeAt: new Date().toISOString(),
        startedFromServerSnapshot: startedFromServerSnapshot ?? existing?.startedFromServerSnapshot ?? false,
        serverUpdatedAt: resetEvaluationId ? null : (serverUpdatedAt !== undefined ? serverUpdatedAt : (existing?.serverUpdatedAt ?? null)),
        serverVersion: resetEvaluationId ? null : (serverVersion !== undefined ? serverVersion : (existing?.serverVersion ?? null)),
        forceOverwrite: forceOverwrite !== undefined ? forceOverwrite : (existing?.forceOverwrite ?? false)
      });
    } catch (err) {
      console.error('Error guardando borrador offline de evaluación:', err);
    }
  },

  async markPendingSync(quotationId, branchId, pendingSync) {
    if (!enabled) return;
    const key = draftKey(quotationId, branchId);
    try {
      const existing = await store.getItem(key);
      if (!existing) return;
      await store.setItem(key, { ...existing, pendingSync });
    } catch (err) {
      console.error('Error actualizando estado de sincronización del borrador:', err);
    }
  },

  /**
   * El servidor rechazó el borrador de forma definitiva (p. ej. 403: el usuario ya no tiene el
   * ente asignado). Deja de reintentarse, pero los datos se conservan en el dispositivo; un
   * nuevo guardado local (saveDraft) lo vuelve a poner en la cola.
   */
  async markSyncRejected(quotationId, branchId, { status, message, code = null, details = null }) {
    if (!enabled) return;
    const key = draftKey(quotationId, branchId);
    try {
      const existing = await store.getItem(key);
      if (!existing) return;
      await store.setItem(key, {
        ...existing,
        pendingSync: false,
        syncRejected: { status, message, code, details, at: new Date().toISOString() }
      });
    } catch (err) {
      console.error('Error marcando borrador rechazado por el servidor:', err);
    }
  },

  async clearDraft(quotationId, branchId) {
    if (!enabled) return;
    try {
      await store.removeItem(draftKey(quotationId, branchId));
    } catch (err) {
      console.error('Error limpiando borrador offline de evaluación:', err);
    }
  },

  /** Conteo para el indicador global: cambios por subir y borradores rechazados por el servidor. */
  async summary() {
    const result = { pending: 0, rejected: 0, rejectedDrafts: [] };
    if (!enabled) return result;
    try {
      await store.iterate((value) => {
        if (value?.pendingSync) result.pending += 1;
        if (value?.syncRejected) {
          result.rejected += 1;
          result.rejectedDrafts.push(value);
        }
      });
    } catch (err) {
      console.error('Error resumiendo borradores offline:', err);
    }
    return result;
  },

  /** Todos los borradores con pendingSync=true, para el motor de sincronización. */
  async listPendingDrafts() {
    const pending = [];
    if (!enabled) return pending;
    try {
      await store.iterate((value) => {
        if (value?.pendingSync) pending.push(value);
      });
    } catch (err) {
      console.error('Error listando borradores pendientes de sincronización:', err);
    }
    return pending;
  }
};

export default offlineEvaluationStore;
